/**
 * Settings — profile (alias + avatar), theme, and links to integrations.
 */

import { useEffect, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useTheme } from '@/features/theme/ThemeProvider'
import { useAuth } from '@/features/auth/authContext'
import { toast } from '@/features/feedback/toast'
import { apiRaw } from '@/lib/api'
import { cn } from '@/lib/utils'
import { UserAvatar } from '@/components/user/UserAvatar'

export function SettingsPage() {
  const { user, updateProfile } = useAuth()
  const { theme, themes, setTheme } = useTheme()
  const [alias, setAlias] = useState(user?.alias ?? '')
  const [avatarUrl, setAvatarUrl] = useState(user?.customAvatarUrl ?? '')
  const [saving, setSaving] = useState(false)

  // Re-hydrate when user changes (e.g. after /me PATCH).
  useEffect(() => {
    setAlias(user?.alias ?? '')
    setAvatarUrl(user?.customAvatarUrl ?? '')
  }, [user?.alias, user?.customAvatarUrl])

  if (!user) return null

  const displayName = user.alias || user.displayName || user.email

  return (
    <div className="mx-auto max-w-3xl space-y-8 p-6">
      <header>
        <h1 className="text-2xl font-semibold">Settings</h1>
        <p className="text-sm text-surface-500">
          Personal preferences and integrations.
        </p>
      </header>

      <ProfileCard
        alias={alias}
        avatarUrl={avatarUrl}
        displayName={displayName}
        email={user.email}
        avatarSources={[user.customAvatarUrl, user.avatarUrl]}
        saving={saving}
        onAlias={setAlias}
        onAvatar={setAvatarUrl}
        onResetAlias={() => setAlias('')}
        onResetAvatar={() => setAvatarUrl('')}
        onSave={async () => {
          setSaving(true)
          try {
            await updateProfile({
              alias: alias.trim() ? alias.trim() : null,
              customAvatarUrl: avatarUrl.trim() ? avatarUrl.trim() : null,
            })
            toast('Profile updated', { kind: 'success' })
          } catch (e) {
            toast((e as Error).message ?? 'Failed to save', { kind: 'error' })
          } finally {
            setSaving(false)
          }
        }}
      />

      <ThemeCard current={theme.id} themes={themes} onChange={setTheme} />

      <SkillCard />

      <NavCard
        title="API tokens"
        description="Mint API keys to access Friday from MCP, Claude Code, or any external integration. Per-user, scoped, revocable."
        to="/settings/tokens"
      />

      <NavCard
        title="GitHub"
        description="Connect a board to its repository. Linked pull requests move tasks to In Review, and to Done after a healthy production release."
        to="/settings/github"
      />

      <NavCard
        title="Notifications"
        description="Manage what events notify you (coming soon)."
        to="/settings"
        disabled
      />
    </div>
  )
}

function ProfileCard({
  alias,
  avatarUrl,
  displayName,
  email,
  avatarSources,
  saving,
  onAlias,
  onAvatar,
  onResetAlias,
  onResetAvatar,
  onSave,
}: {
  alias: string
  avatarUrl: string
  displayName: string
  email?: string | null
  avatarSources: (string | null | undefined)[]
  saving: boolean
  onAlias: (v: string) => void
  onAvatar: (v: string) => void
  onResetAlias: () => void
  onResetAvatar: () => void
  onSave: () => void
}) {
  return (
    <section className="card p-4">
      <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-surface-500">
        Profile
      </h2>
      <p className="mb-4 text-xs text-surface-500">
        Your alias and avatar override the values from the auth service. Leave them empty to fall back.
      </p>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
        <div className="flex flex-col items-center gap-2">
          <UserAvatar name={displayName} email={email} sources={avatarSources} className="h-16 w-16 text-lg" pixelSize={128} />
          <span className="text-[10px] text-surface-500">{displayName}</span>
        </div>
        <div className="flex-1 space-y-3">
          <Field label="Alias" hint="Shown in the sidebar and on comments. Falls back to your display name.">
            <div className="flex items-center gap-2">
              <input
                className="input"
                value={alias}
                onChange={(e) => onAlias(e.target.value)}
                placeholder="e.g. Maoz"
                maxLength={60}
              />
              {alias && (
                <button type="button" className="btn-ghost text-xs" onClick={onResetAlias}>
                  Use default
                </button>
              )}
            </div>
          </Field>
          <Field label="Avatar URL" hint="Square images look best. Leave blank to use the auth avatar, then your Gravatar.">
            <div className="flex items-center gap-2">
              <input
                className="input"
                value={avatarUrl}
                onChange={(e) => onAvatar(e.target.value)}
                placeholder="https://…/avatar.png"
                type="url"
              />
              {avatarUrl && (
                <button type="button" className="btn-ghost text-xs" onClick={onResetAvatar}>
                  Use default
                </button>
              )}
            </div>
          </Field>
          <button type="button" className="btn-primary" onClick={onSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save profile'}
          </button>
        </div>
      </div>
    </section>
  )
}

function ThemeCard({
  current,
  themes,
  onChange,
}: {
  current: string
  themes: ReturnType<typeof useTheme>['themes']
  onChange: (id: string) => void
}) {
  return (
    <section className="card p-4">
      <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-surface-500">
        Theme
      </h2>
      <p className="mb-3 text-xs text-surface-500">
        Each theme is a complete design — not just a colour swap. Some change the typeface and radius. Switch any time; we save your choice locally.
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {themes.map((t) => (
          <button
            key={t.id}
            onClick={() => onChange(t.id)}
            className={cn(
              'group relative flex items-start gap-3 rounded-lg border p-3 text-left transition-all hover:shadow-md',
              current === t.id
                ? 'border-brand-500 bg-brand-500/5'
                : 'border-surface-200 dark:border-surface-700 hover:border-surface-300 dark:hover:border-surface-600',
            )}
          >
            <ThemePreview theme={t} />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold">{t.name}</div>
              <div className="line-clamp-2 text-xs text-surface-500">
                {t.description}
              </div>
            </div>
            {current === t.id && (
              <span className="absolute right-2 top-2 rounded-full bg-brand-500 px-1.5 py-0.5 text-[10px] font-medium text-white">
                Active
              </span>
            )}
          </button>
        ))}
      </div>
    </section>
  )
}

function ThemePreview({ theme }: { theme: ReturnType<typeof useTheme>['themes'][number] }) {
  const style = {
    backgroundColor: `rgb(${theme.tokens.bg})`,
    borderColor: `rgb(${theme.tokens.border})`,
    color: `rgb(${theme.tokens.fg})`,
  } as React.CSSProperties
  return (
    <div
      className="flex h-14 w-20 shrink-0 flex-col gap-1 border p-1.5 text-[8px] font-medium"
      style={{ ...style, borderRadius: theme.tokens.radiusMd }}
    >
      <div
        className="h-2 w-8"
        style={{ backgroundColor: `rgb(${theme.tokens.accent})`, borderRadius: theme.tokens.radiusSm }}
      />
      <div
        className="h-1.5 w-12"
        style={{ backgroundColor: `rgb(${theme.tokens.fgMuted})`, borderRadius: theme.tokens.radiusSm, opacity: 0.6 }}
      />
      <div
        className="mt-auto h-3 w-full"
        style={{
          backgroundColor: `rgb(${theme.tokens.bgElevated})`,
          borderRadius: theme.tokens.radiusSm,
          border: `1px solid rgb(${theme.tokens.border})`,
        }}
      />
    </div>
  )
}

function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-surface-600 dark:text-surface-300">{label}</span>
      {hint ? <span className="mt-0.5 block text-[10px] text-surface-500">{hint}</span> : null}
      <div className="mt-1">{children}</div>
    </label>
  )
}

function NavCard({
  title,
  description,
  to,
  disabled,
}: {
  title: string
  description: string
  to: string
  disabled?: boolean
}) {
  if (disabled) {
    return (
      <section className="card p-4 opacity-60">
        <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-surface-500">
          {title}
        </h2>
        <p className="text-sm text-surface-500">{description}</p>
      </section>
    )
  }
  return (
    <Link to={to} className="card block p-4 hover:border-brand-500">
      <h2 className="mb-1 text-sm font-semibold">{title}</h2>
      <p className="text-xs text-surface-500">{description}</p>
    </Link>
  )
}

const MCP_SNIPPET = `{
  "mcpServers": {
    "friday": {
      "type": "http",
      "url": "https://mcp.friday.thriv.es",
      "headers": { "Authorization": "Bearer \${FRIDAY_API_TOKEN}" }
    }
  }
}`

function SkillCard() {
  const [downloading, setDownloading] = useState(false)

  async function downloadSkill() {
    if (downloading) return
    setDownloading(true)
    try {
      // `apiRaw` attaches Authorization + handles one refresh-and-retry on 401.
      // A plain <a download href> doesn't include the JWT, so the server's
      // authMiddleware would 401 the request.
      const res = await apiRaw('/api/v1/me/skill/download')
      if (!res.ok) {
        throw new Error(`Download failed: HTTP ${res.status}`)
      }
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'friday-product-manager.zip'
      document.body.appendChild(a)
      a.click()
      a.remove()
      // Defer revoke so Safari has time to start the download.
      setTimeout(() => URL.revokeObjectURL(url), 30_000)
      toast('Skill downloaded', { kind: 'success' })
    } catch (e) {
      toast((e as Error).message ?? 'Failed to download skill', { kind: 'error' })
    } finally {
      setDownloading(false)
    }
  }

  return (
    <section className="card p-4">
      <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-surface-500">
        Product Manager skill
      </h2>
      <p className="mb-3 text-xs text-surface-500">
        Download a Claude skill that lets an AI agent manage your tasks, projects, sprints,
        comments, and notebook pages. Unzip into any project's{' '}
        <code className="mx-0.5 rounded bg-surface-100 px-1 py-0.5 text-[10px] dark:bg-surface-800">
          .claude/skills/
        </code>{' '}
        folder and provide a Friday API token.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          className="btn-primary"
          onClick={() => void downloadSkill()}
          disabled={downloading}
        >
          {downloading ? 'Downloading…' : 'Download skill (.zip)'}
        </button>
        <button
          type="button"
          className="btn-ghost text-xs"
          onClick={() => {
            void navigator.clipboard
              ?.writeText(MCP_SNIPPET)
              .then(() => toast('.mcp.json snippet copied', { kind: 'success' }))
              .catch(() => toast('Could not copy — select and copy manually', { kind: 'error' }))
          }}
        >
          Copy .mcp.json snippet
        </button>
      </div>
    </section>
  )
}